import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Title } from "@angular/platform-browser";


import { BehaviorSubject, EMPTY, Observable, Subscription } from "rxjs";
import { catchError, finalize, map, tap } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import {
  AppState,
  loadedMenusState,
} from "src/app/store/reducers";

import { TranslateService } from "@ngx-translate/core";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";
import { ProductFamilyService } from "src/app/shared/services/products/productFamily/productFamily.service";

import {
  PageEvent,
  SortEvent,
  SearchEvent,
  FilterEvent,
  DefaultPage,
  ColumnSorterInfo,
  TableConfig,
  FieldType,
  FormatType,
  ConfigSettingType,
  TableEvent,
  ExportEvent,
  ConfigScope,
} from "src/app/models/custom-table/custom-table.model";
import { MenuDTO, RouteData } from "src/app/models/Menu/MenuDTO";
import { subMenusIterator } from "src/app/shared/utils/menuUtils";
import { ProductFamilyExtendedDTO } from "src/app/models/Products/productDTO";
import { setSnackbar } from "src/app/store/actions/messages/message.actions";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { getColorByBgColor } from "src/app/shared/utils/colorUtils";

const USAGE_CODE = "PRODUCT_FAMILY";

@Component({
  selector: "app-product-family-list",
  templateUrl: "./product-family-list.component.html",
  styleUrls: ["./product-family-list.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ProductFamilyListComponent implements OnInit, OnDestroy {
  isLoading$ = new BehaviorSubject<boolean>(false);
  totalProductFamilies$ = new BehaviorSubject<number>(0);
  columnInfo$: Observable<ColumnSorterInfo[]>;

  productFamiliesListSub: Subscription;
  excelProductFamiliesList$: Observable<ProductFamilyExtendedDTO[]>;

  formPath: string = "families/family";
  tableTitle: string;
  headerIcon: string;

  menus$: Observable<MenuDTO[]>;
  menuSub: Subscription;

  tableData: ProductFamilyExtendedDTO[];

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Entity.token",
      field: "token",
      fieldType: FieldType.STRING,
      hidden: true,
      identifier: true,
    },
    {
      header: "ProductFamily.code",
      field: "code",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "ProductFamily.description",
      field: "detailedDescription",
      fieldType: FieldType.OBJECT_DESCRIPTION,
      searchable: true,
    },
    {
      header: "Products.parentProductFamilyCode",
      field: "parentProductFamilyCode",
      fieldType: FieldType.STRING,
    },
    {
      header: "ProductFamily.childs",
      field: "childs",
      fieldType: FieldType.ARRAY,
      noAdvanceFiltering: true,
    },
    {
      header: "Products.imageUrl",
      field: "imageUrl",
      fieldType: FieldType.IMAGE,
    },
    {
      header: "Products.color",
      field: "color",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "ProductFamily.numberOfProducts",
      field: "numberOfProducts",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "SiteManagement.Products",
      field: "products",
      fieldType: FieldType.ARRAY,
      noAdvanceFiltering: true,
    },
    {
      header: "Categories.itemAllowed",
      field: "itemAllowed",
      fieldType: FieldType.BOOLEAN,
    },
    {
      header: "Tax.createdOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME
    },
    {
      header: "Tax.updatedOn",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME
    },
  ];

  // loaded locale
  public locale: string = localStorage.getItem("locale");

  constructor(
    private store: Store<AppState>,
    private titleService: Title,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private productFamilyService: ProductFamilyService,
    private tableConfigService: TableConfigService
  ) {}

  ngOnInit(): void {
    this.menus$ = this.store.pipe(select(loadedMenusState));

    this.loadScreenTitle();
    this.getTableConfig();
    this.getProductFamiliesList();
  }

  loadScreenTitle() {
    this.menuSub = this.menus$.subscribe((menus: MenuDTO[]) => {
      if (menus && menus.length) {
        this.route.data.subscribe((routeData: RouteData) => {
          menus.forEach((menuEl: MenuDTO) => {
            const selectedMenu = subMenusIterator(menuEl, routeData);
            if (selectedMenu) {
              this.headerIcon = selectedMenu.icon;
              this.tableTitle = selectedMenu.description;
              this.titleService.setTitle(this.tableTitle);
            }
          });
        });
      }
    });
  }

  getProductFamiliesList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.isLoading$.next(true);
    this.productFamiliesListSub = this.productFamilyService
      .getProductFamiliesList(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent,
        filterEvent
      )
      .pipe(
        map((result) => {
          const { items, size } = result;
          this.totalProductFamilies$.next(size);
          return this.setTableCustomElements(items);
        }),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("ProductFamily.loadError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      ).subscribe((result: ProductFamilyExtendedDTO[]) => this.tableData = result);
  }

  getProductFamiliesExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.isLoading$.next(true);
    this.excelProductFamiliesList$ = this.productFamilyService
      .getProductFamiliesList(
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
        },
        searchEvent,
        sortEvent,
        filterEvent
      )
      .pipe(
        map(({ items }) => this.setTableCustomElements(items, true)),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("ProductFamily.loadError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      );
  }

  getTableConfig() {
    this.columnInfo$ = this.tableConfigService
      .getConfigSettings(USAGE_CODE, ConfigSettingType.TABLE_CONFIG)
      .pipe(
        map(
          (response) =>
            <ColumnSorterInfo[]>response.result?.value?.tableColumnInfo
        ),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.LoadError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      );
  }

  onGetTableData(tableEvent: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.getProductFamiliesList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onGetExportData(exportEvent: ExportEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    this.getProductFamiliesExcelList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.isLoading$.next(true);
    this.tableConfigService
      .saveConfigSettings({
        code: USAGE_CODE,
        settingTypeCode: ConfigSettingType.TABLE_CONFIG,
        scope: ConfigScope.USER,
        value: { tableColumnInfo },
      })
      .pipe(
        tap(() =>
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveSuccess"),
                SnackbarType.SUCCESS
              )
            )
          )
        ),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }

  setTableCustomElements(productFamilies: ProductFamilyExtendedDTO[], excel = false): any[] {
    return productFamilies.map((productFamily) => {
      const { products, childs, color } = productFamily;
      const bgColor = getColorByBgColor(`#${color}`);
      return {
        ...productFamily,
        products: products?.map(({ description }) => ({
          arrayDescription: description,
        })),
        childs: childs?.map(({ code }) => ({
          arrayDescription: code,
        })),
        color: excel 
          ? color 
          : {
            value: color,
            icon: "format_color_fill",
            style: `background-color: #${color}; color: ${bgColor};`,
          },
      };
    });
  }

  ngOnDestroy(): void {
    if (this.menuSub) {
      this.menuSub.unsubscribe();
    }
    if (this.productFamiliesListSub) {
      this.productFamiliesListSub.unsubscribe();
    }
  }
}
